import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Box } from "@chakra-ui/react";
import CertificationList from "./certificationList";
import CertificationDrawer from "./certificationDrawer";

const DEFAULT_FILTERS = { status: "all", entityType: "all", search: "" };
const DEFAULT_PAGINATION = { page: 1, pages: 1, total: 0 };

/**
 * Builds the query params object for the certifications list endpoint.
 * "all" filter values are omitted so the server returns unfiltered data.
 * @param {Object} filters - Current filter state
 * @param {number} page - Current page (1-indexed)
 * @returns {Object} Axios params object
 */
function buildListParams(filters, page) {
    const params = { page };
    if (filters.status && filters.status !== "all") params.status = filters.status;
    if (filters.entityType && filters.entityType !== "all") params.entity_type = filters.entityType;
    if (filters.search && filters.search.trim()) params.search = filters.search.trim();
    return params;
}

/**
 * Fetches certifications from the /admin/certifications/list endpoint and pushes
 * the paginated payload (data, page, pages, total) into local state.
 * @param {Object} args - { filters, page, setLoading, setCertifications, setPagination }
 * @returns {Promise<void>}
 */
async function fetchCertificationsData(args) {
    const { filters, page, setLoading, setCertifications, setPagination } = args;
    setLoading(true);
    try {
        const response = await axios.get("/admin/certifications/list", { params: buildListParams(filters, page) });
        const payload = response.data || {};
        const items = Array.isArray(payload.data) ? payload.data : [];
        setCertifications(items);
        setPagination({ page: payload.page || 1, pages: payload.pages || 1, total: payload.total || 0 });
    } catch (error) {
        console.error("Error fetching certifications:", error);
        toast.error("Erreur lors du chargement des certifications");
    } finally {
        setLoading(false);
    }
}

/**
 * Merges partial filter changes into state and resets pagination to page 1.
 * @param {Object} partial - Subset of filter fields to update
 * @param {Function} setFiltersState - Filters state setter
 * @param {Function} setPage - Page state setter
 */
function updateFilters(partial, setFiltersState, setPage) {
    setFiltersState(prev => ({ ...prev, ...partial }));
    setPage(1);
}

/**
 * Replaces an updated certification in the list and keeps it selected so the
 * open drawer reflects the refreshed payload.
 * @param {Object} updated - The certification returned by the API
 * @param {Function} setCertifications - Setter for the certifications list
 * @param {Function} setSelected - Setter for the selected certification
 */
function applyCertUpdate(updated, setCertifications, setSelected) {
    setCertifications(prev => prev.map(cert => (cert.id === updated.id ? updated : cert)));
    setSelected(updated);
}

/**
 * Main certifications management page: a full-width list with a right-side
 * detail drawer. Owns filter, page, pagination, and selection state.
 * @returns {React.ReactElement}
 */
function Certifications() {
    const [certifications, setCertifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [filters, setFiltersState] = useState(DEFAULT_FILTERS);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
    const fetchCertifications = useCallback(() => fetchCertificationsData({ filters, page, setLoading, setCertifications, setPagination }), [filters, page]);
    const setFilters = useCallback((partial) => updateFilters(partial, setFiltersState, setPage), []);
    const onUpdated = useCallback((updated) => applyCertUpdate(updated, setCertifications, setSelected), []);
    useEffect(() => { fetchCertifications(); }, [fetchCertifications]);
    return (
        <Box>
            <CertificationList certifications={certifications} selectedCertification={selected}
                onRowClick={setSelected} loading={loading} refreshCertifications={fetchCertifications}
                filters={filters} setFilters={setFilters} pagination={pagination} page={page} setPage={setPage} />
            <CertificationDrawer certification={selected} onClose={() => setSelected(null)} onUpdated={onUpdated} />
        </Box>
    );
}

export default Certifications;
